'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, CheckCircle2, XCircle, RotateCcw, Clock, AlertTriangle, ChevronDown, Filter,  } from 'lucide-react';
import {
  MOCK_REQUISITIONS, REQUISITION_STATUS_STYLES, PRIORITY_STYLES, formatUGX,
  PurchaseRequisition, RequisitionStatus,
} from '../../components/procurementData';
import { useRole } from '@/lib/useRole';
import BudgetTracker, { BudgetSummaryPanel } from '../../components/BudgetTracker';

type ApprovalFilter = 'all' | 'pending' | 'approved' | 'rejected';

function ApprovalCard({ req, onApprove, onReject, onReturn }: {
  req: PurchaseRequisition;
  onApprove: (id: string, comment: string) => void;
  onReject: (id: string, comment: string) => void;
  onReturn: (id: string, comment: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [action, setAction] = useState<'approve' | 'reject' | 'return' | null>(null);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');

  const st = REQUISITION_STATUS_STYLES[req.status];
  const pri = PRIORITY_STYLES[req.priority];
  const pendingApprover = req.approvals.find(a => a.action === 'pending');

  function handleAction() {
    if ((action === 'reject' || action === 'return') && !comment.trim()) {
      setCommentError('Comment is required when rejecting or returning.');
      return;
    }
    setCommentError('');
    if (action === 'approve') onApprove(req.id, comment);
    else if (action === 'reject') onReject(req.id, comment);
    else if (action === 'return') onReturn(req.id, comment);
    setAction(null);
    setComment('');
  }

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden transition-all ${req.status === 'pending_approval' ? 'border-amber-200 shadow-sm' : 'border-gray-100'}`}>
      {/* Card Header */}
      <div className="p-4 flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${req.status === 'pending_approval' ? 'bg-amber-100' : req.status === 'approved' ? 'bg-green-100' : 'bg-red-100'}`}>
          {req.status === 'pending_approval' ? <Clock size={18} className="text-amber-600" /> :
           req.status === 'approved' ? <CheckCircle2 size={18} className="text-green-600" /> :
           <XCircle size={18} className="text-red-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900">{req.requisitionNumber}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pri.bg} ${pri.text}`}>{pri.label}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{req.department} · {req.requestedBy} · {req.requestDate}</div>
          <div className="text-xs text-gray-600 mt-1 line-clamp-2">{req.purpose}</div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className="text-sm font-bold text-gray-900">{formatUGX(req.estimatedTotal)}</div>
          <div className="text-xs text-gray-400">{req.items.length} item{req.items.length !== 1 ? 's' : ''}</div>
          <button onClick={() => setExpanded(!expanded)} className="p-1 rounded hover:bg-gray-100 transition-colors mt-1">
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Budget Tracker — compact, always visible for pending */}
      {req.status === 'pending_approval' && (
        <div className="px-4 pb-3">
          <BudgetTracker
            budgetCode={req.budgetCode}
            requestAmount={req.estimatedTotal}
            currency={req.currency}
            compact={true}
          />
        </div>
      )}

      {/* Approval Steps */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {req.approvals.map((ap, idx) => (
            <React.Fragment key={ap.id}>
              {idx > 0 && <div className="w-6 h-px bg-gray-200 flex-shrink-0" />}
              <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg flex-shrink-0 text-xs font-semibold ${
                ap.action === 'approved' ? 'bg-green-50 text-green-700' :
                ap.action === 'rejected' ? 'bg-red-50 text-red-700' :
                ap.action === 'pending'? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-gray-50 text-gray-500'
              }`}>
                {ap.action === 'approved' ? <CheckCircle2 size={11} /> : ap.action === 'rejected' ? <XCircle size={11} /> : <Clock size={11} />}
                <span>{ap.approverRole}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/30">
          <div className="grid grid-cols-2 gap-3 mb-3">
            {req.items.map(item => (
              <div key={item.id} className="bg-white rounded-xl p-3 border border-gray-100">
                <div className="text-xs font-semibold text-gray-800">{item.description}</div>
                <div className="text-xs text-gray-400 mt-0.5">{item.quantity} {item.unit} × {formatUGX(item.estimatedUnitCost)}</div>
                <div className="text-xs font-bold text-gray-700 mt-1">{formatUGX(item.estimatedTotal)}</div>
              </div>
            ))}
          </div>
          {req.notes && (
            <div className="text-xs text-gray-500 bg-white rounded-xl p-3 border border-gray-100">
              <span className="font-semibold text-gray-600">Notes: </span>{req.notes}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons — only for pending */}
      {req.status === 'pending_approval' && (
        <div className="border-t border-gray-100 px-4 py-3">
          {!action ? (
            <div className="flex gap-2">
              <button onClick={() => setAction('approve')} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-600 text-white text-xs font-semibold rounded-xl hover:bg-green-700 transition-colors">
                <CheckCircle2 size={13} /> Approve
              </button>
              <button onClick={() => setAction('return')} className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-amber-300 text-amber-700 text-xs font-semibold rounded-xl hover:bg-amber-50 transition-colors">
                <RotateCcw size={13} /> Return
              </button>
              <button onClick={() => setAction('reject')} className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-red-300 text-red-600 text-xs font-semibold rounded-xl hover:bg-red-50 transition-colors">
                <XCircle size={13} /> Reject
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-600">
                {action === 'approve' ? 'Approve Requisition' : action === 'reject' ? 'Reject Requisition' : 'Return for Revision'}
                {(action === 'reject' || action === 'return') && <span className="text-red-500 ml-1">*</span>}
              </div>
              <textarea
                value={comment}
                onChange={e => { setComment(e.target.value); setCommentError(''); }}
                placeholder={action === 'approve' ? 'Optional comment...' : 'Comment required — explain your decision...'}
                rows={2}
                className={`w-full px-3 py-2 text-xs border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none ${commentError ? 'border-red-400' : 'border-gray-200'}`}
              />
              {commentError && <p className="text-xs text-red-500">{commentError}</p>}
              <div className="flex gap-2">
                <button onClick={handleAction} className={`flex-1 py-2 text-xs font-semibold rounded-xl text-white transition-colors ${action === 'approve' ? 'bg-green-600 hover:bg-green-700' : action === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                  Confirm {action.charAt(0).toUpperCase() + action.slice(1)}
                </button>
                <button onClick={() => { setAction(null); setComment(''); setCommentError(''); }} className="px-4 py-2 text-xs font-semibold border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-600">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApprovalsContent() {
  const { can } = useRole();
  const [filter, setFilter] = useState<ApprovalFilter>('pending');
  const [reqs, setReqs] = useState(MOCK_REQUISITIONS);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const canApprove = can('manage_procurement');

  function showToast(msg: string, type: 'success' | 'error' | 'warning' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  function handleApprove(id: string, comment: string) {
    setReqs(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' as RequisitionStatus } : r));
    showToast('Requisition approved. Procurement team has been notified.');
  }

  function handleReject(id: string, comment: string) {
    setReqs(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' as RequisitionStatus } : r));
    showToast('Requisition rejected. Requester has been notified.', 'error');
  }

  function handleReturn(id: string, comment: string) {
    setReqs(prev => prev.map(r => r.id === id ? { ...r, status: 'returned' as RequisitionStatus } : r));
    showToast('Requisition returned for revision.', 'warning');
  }

  const filtered = reqs.filter(r => {
    if (filter === 'pending') return r.status === 'pending_approval';
    if (filter === 'approved') return r.status === 'approved' || r.status === 'completed';
    if (filter === 'rejected') return r.status === 'rejected' || r.status === 'returned';
    return true;
  });

  const pendingCount = reqs.filter(r => r.status === 'pending_approval').length;
  const pendingBudgetCodes = [...new Set(reqs.filter(r => r.status === 'pending_approval').map(r => r.budgetCode))];

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-lg mx-auto">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm ${toast.type === 'success' ? 'bg-gray-900' : toast.type === 'error' ? 'bg-red-700' : 'bg-amber-600'}`}>
          <span className="w-2 h-2 rounded-full bg-white/60 flex-shrink-0" />
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link href="/procurement" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} className="text-gray-500" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900">Approval Workflow</h1>
            {pendingCount > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">{pendingCount} pending</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Review and action purchase requisitions awaiting approval</p>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <div className="mb-5 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={16} className="flex-shrink-0 text-amber-500" />
          <span><strong>{pendingCount} requisition{pendingCount > 1 ? 's' : ''}</strong> {pendingCount > 1 ? 'are' : 'is'} awaiting your approval. Please review and take action.</span>
        </div>
      )}

      {/* Budget Summary for pending items */}
      {filter === 'pending' && pendingBudgetCodes.length > 0 && (
        <div className="mb-5">
          <BudgetSummaryPanel budgetCodes={pendingBudgetCodes} showSettings={canApprove} />
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
        {([
          { id: 'pending', label: 'Pending', count: reqs.filter(r => r.status === 'pending_approval').length },
          { id: 'approved', label: 'Approved', count: reqs.filter(r => r.status === 'approved' || r.status === 'completed').length },
          { id: 'rejected', label: 'Rejected / Returned', count: reqs.filter(r => r.status === 'rejected' || r.status === 'returned').length },
          { id: 'all', label: 'All', count: reqs.length },
        ] as { id: ApprovalFilter; label: string; count: number }[]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${filter === tab.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab.label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${filter === tab.id ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-500'}`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <CheckCircle2 size={40} className="mx-auto mb-3 text-gray-200" />
          <div className="text-sm font-semibold">No {filter === 'pending' ? 'pending approvals' : filter === 'approved' ? 'approved requisitions' : 'rejected requisitions'} found.</div>
          {filter === 'pending' && <div className="text-xs mt-1">All requisitions are up to date.</div>}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => (
            <ApprovalCard
              key={req.id}
              req={req}
              onApprove={handleApprove}
              onReject={handleReject}
              onReturn={handleReturn}
            />
          ))}
        </div>
      )}
    </div>
  );
}
